import { Col, Divider, Icon, Row, Sidepanel, Spinner2, Text } from 'components';
import { theme } from 'lib';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PollProfileCard from './PollProfileCard';

const SCROLL_ID = 'polls-list-scroll';

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${theme.GREY_60};
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${theme.GREY_10};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: ${theme.GREY_20};
  }
`;

// Stretch each fixed-width card to the panel column for the vertical list.
const FullWidthSlot = styled.div`
  width: 100%;
  & > button,
  & > div {
    width: 100%;
    min-width: 0;
  }
`;

const Footer = styled.div`
  padding: 16px 0 8px;
  display: flex;
  justify-content: center;
  color: ${theme.GREY_60};
  font-size: 12px;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;

/**
 * PollsListPanel — right-side Sidepanel listing every poll for this team.
 * Opened from the profile row's "See all". Reuses PollProfileCard full-width so
 * admins can manage from here too. Infinite scroll via IntersectionObserver.
 */
const PollsListPanel = ({
  visible,
  polls,
  loading,
  hasMore,
  onLoadMore,
  canManage,
  onClose,
  onView,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!visible || !hasMore || !sentinelRef.current) return;
    const root = document.getElementById(SCROLL_ID);
    if (!root) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore && onLoadMore();
      },
      { root, rootMargin: '200px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [visible, hasMore, onLoadMore, polls.length]);

  return (
    <Sidepanel visible={visible} wid={520} pad="0" closeModal={() => {}} contentId={SCROLL_ID}>
      <Col pos="sticky" bg="#fff" top="0" zIndex={2} wid="100%">
        <Row between center wid="100%" pad="14px 20px">
          <Text h3>{t('pollQuizProfile.allPolls')}</Text>
          <CloseBtn type="button" aria-label={t('pollQuizProfile.close')} onClick={onClose}>
            <Icon type="close" dimensions={14} col={theme.GREY_80} />
          </CloseBtn>
        </Row>
        <Divider />
      </Col>

      <Col wid="100%" pad="16px 20px 24px" gap="12px">
        {loading && polls.length === 0 ? (
          <Col centerAll pad="40px 0">
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        ) : polls.length === 0 ? (
          <Empty>
            <Text col={theme.GREY_60}>{t('pollQuizProfile.noPollsYet')}</Text>
          </Empty>
        ) : (
          <>
            {polls.map((p) => (
              <FullWidthSlot key={p._id}>
                <PollProfileCard
                  poll={p}
                  isMobile={false}
                  canManage={canManage}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleActive={onToggleActive}
                />
              </FullWidthSlot>
            ))}

            <Sentinel ref={sentinelRef} />

            {hasMore ? (
              <Footer>
                <Spinner2 col={theme.SECONDARY} />
              </Footer>
            ) : (
              polls.length > 0 && <Footer>{t('pollQuizProfile.seenThemAll')}</Footer>
            )}
          </>
        )}
      </Col>
    </Sidepanel>
  );
};

export default PollsListPanel;
